import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  ACTIONS_BY_STATUS, REASON_TEXT, SEVERITY_CLASS, STATUS_LABELS, TABS, currentSiteId, tabForStatus, visionCall,
} from './VisualSafetyShared';
import { LayoutEditor, RulesEditor } from './VisualSafetyConfig';

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const ConcernDetail = ({ concern, where, onChanged }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const actions = ACTIONS_BY_STATUS[concern.status] || [];
  const fieldAction = actions.find((a) => a.field);
  const run = async (a) => {
    setBusy(a.action);
    setError('');
    try {
      await visionCall(`/concerns/${encodeURIComponent(concern.id)}/${a.action}`, {
        method: 'POST', body: a.field ? { [a.field]: value.trim() } : {},
      });
      setValue('');
      await onChanged();
    } catch (err) {
      setError(err.message || 'That did not work.');
    } finally {
      setBusy('');
    }
  };
  return (
    <section aria-label="Concern detail" className="border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-gray-900">{concern.title}</h2>
        <span className={`px-2 py-0.5 text-xs font-medium ${SEVERITY_CLASS[concern.severity] || SEVERITY_CLASS.low}`}>{concern.severity}</span>
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">{STATUS_LABELS[concern.status] || concern.status}</span>
        {concern.staged && <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-900">Staged demo</span>}
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-gray-700 sm:grid-cols-2">
        <div><dt className="inline font-medium">Where: </dt><dd className="inline">{where}</dd></div>
        <div><dt className="inline font-medium">Procedure: </dt><dd className="inline">{concern.sop_ref} (rule v{concern.rule_version})</dd></div>
        <div><dt className="inline font-medium">First seen: </dt><dd className="inline">{when(concern.first_seen_at)}</dd></div>
        <div><dt className="inline font-medium">Seen: </dt><dd className="inline">{concern.occurrences} time{concern.occurrences === 1 ? '' : 's'}, last {when(concern.last_seen_at)}</dd></div>
        <div><dt className="inline font-medium">Responder: </dt><dd className="inline">{concern.responder_role}</dd></div>
        <div><dt className="inline font-medium">Sign-off by: </dt><dd className="inline">{concern.approver_role}</dd></div>
      </dl>
      <p className="mt-3 border-l-4 border-gray-300 bg-gray-50 p-3 text-sm text-gray-800">
        <span className="font-medium">What the camera established: </span>{REASON_TEXT[concern.reason] || concern.reason}
        {' '}It does not show that a rule was broken, that food is unsafe, or that anything else is fine.
      </p>
      {concern.resolution_note && <p className="mt-3 text-sm text-gray-800"><span className="font-medium">What was done: </span>{concern.resolution_note}</p>}
      {actions.length > 0 && (
        <div className="mt-4">
          {fieldAction && (
            <label className="block text-sm text-gray-700">{fieldAction.prompt}
              <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label={fieldAction.prompt}
                className="mt-1 block h-10 w-full border border-gray-300 px-2" />
            </label>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {actions.map((a) => (
              <button key={a.action} type="button" onClick={() => run(a)}
                disabled={Boolean(busy) || (Boolean(a.field) && !value.trim())}
                className="h-10 border border-gray-900 px-4 text-sm text-gray-900 hover:bg-gray-50 disabled:opacity-50">{a.label}</button>
            ))}
          </div>
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      <h3 className="mt-5 text-sm font-semibold text-gray-900">History</h3>
      <ol className="mt-2 space-y-1 text-sm text-gray-700">
        {(concern.history || []).map((h, i) => (
          <li key={`${h.at}-${i}`}>{when(h.at)}: {h.action.replace(/_/g, ' ')} by {h.actor}{h.reason ? ` (${h.reason})` : ''}{h.note ? ` (${h.note})` : ''}</li>
        ))}
      </ol>
    </section>
  );
};

const DemoPanel = ({ onChanged }) => {
  const [scenarios, setScenarios] = useState([]);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    let live = true;
    visionCall('/demo/scenarios').then((r) => { if (live) setScenarios(r.scenarios || []); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const run = async (key, path, body, done) => {
    setBusy(key);
    setMessage('');
    try {
      await visionCall(path, { method: 'POST', body });
      setMessage(done);
      await onChanged();
    } catch (err) {
      setMessage(err.message || 'That did not work.');
    } finally {
      setBusy('');
    }
  };
  return (
    <section aria-label="Staged demo" className="mt-5 border border-indigo-200 bg-indigo-50 p-4">
      <h2 className="text-sm font-semibold text-indigo-950">Staged demo</h2>
      <p className="mt-1 text-sm text-indigo-950">
        These scenarios are staged and clearly labelled. They run through the same path a real camera uses, but nothing here
        is real detection.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {scenarios.map((s) => (
          <button key={s.id} type="button" title={s.watch} disabled={Boolean(busy)}
            onClick={() => run(s.id, '/demo/run', { scenario_id: s.id }, `Played: ${s.title}`)}
            className="h-10 border border-indigo-900 bg-white px-3 text-sm text-indigo-950 disabled:opacity-50">{s.title}</button>
        ))}
        <button type="button" disabled={Boolean(busy)} onClick={() => run('setup', '/demo/setup', undefined, 'Demo cameras set up.')}
          className="h-10 border border-gray-400 bg-white px-3 text-sm text-gray-800 disabled:opacity-50">Set up demo cameras</button>
        <button type="button" disabled={Boolean(busy)} onClick={() => run('clear', '/demo/clear', undefined, 'Staged data cleared.')}
          className="h-10 border border-gray-400 bg-white px-3 text-sm text-gray-800 disabled:opacity-50">Clear staged data</button>
      </div>
      {message && <p role="status" className="mt-2 text-sm text-indigo-950">{message}</p>}
    </section>
  );
};

const VisualSafety = () => {
  const [params] = useSearchParams();
  const wanted = params.get('concern') || '';
  const [siteId, setSiteId] = useState(currentSiteId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('review');
  const [selectedId, setSelectedId] = useState('');
  const deepLinked = useRef(false);

  useEffect(() => {
    const update = () => setSiteId(currentSiteId());
    for (const event of ['locationChanged', 'organizationChanged', 'storage']) window.addEventListener(event, update);
    return () => { for (const event of ['locationChanged', 'organizationChanged', 'storage']) window.removeEventListener(event, update); };
  }, []);

  const load = useCallback(async () => {
    if (!siteId) {
      setData(null);
      setLoading(false);
      setError('Select an authorized site to view visual safety.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [caps, coverage, concerns, layout, rules] = await Promise.all([
        visionCall('/capabilities'), visionCall('/coverage'), visionCall('/concerns?limit=200'),
        visionCall('/layout'), visionCall('/rules'),
      ]);
      setData({ caps, coverage: coverage.cameras || [], concerns: concerns.concerns || [], layout, rules });
    } catch (err) {
      setData(null);
      setError(err.status === 403
        ? 'Visual safety is not switched on for this organisation, or your role cannot open it.'
        : err.message || 'Could not load visual safety.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data || !wanted || deepLinked.current) return;
    const found = data.concerns.find((c) => c.id === wanted);
    if (found) {
      deepLinked.current = true;
      setSelectedId(found.id);
      setTab(tabForStatus(found.status));
    }
  }, [data, wanted]);

  const names = (concern) => {
    const camera = data?.layout?.cameras?.[concern.camera_id];
    const zone = camera?.zones?.[concern.zone_id];
    return [camera?.name || concern.camera_id, zone?.name || concern.zone_id].filter(Boolean).join(' / ');
  };
  const counts = (statuses) => (data ? data.concerns.filter((c) => statuses.includes(c.status)).length : 0);
  const active = TABS.find((t) => t.id === tab);
  const visible = data && active ? data.concerns.filter((c) => active.statuses.includes(c.status)) : [];
  const selected = data?.concerns.find((c) => c.id === selectedId) || null;
  const isConfigTab = tab === 'rules' || tab === 'layout';

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck aria-hidden="true" className="h-6 w-6 text-emerald-700" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Visual safety</h1>
            <p className="text-sm text-gray-600">Camera observations, reviewed by a person and followed through to a corrected check</p>
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} aria-label="Refresh visual safety" title="Refresh visual safety"
          className="inline-flex h-10 w-10 items-center justify-center rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw aria-hidden="true" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <p className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950">
        A camera finding is a prompt to check. It does not show that a rule was broken or that food is unsafe, and an area
        the cameras cannot see is never shown as compliant.
      </p>

      {error && (
        <div role="alert" className="mt-5 flex items-start gap-2 border-l-4 border-amber-600 bg-amber-50 p-3 text-sm text-amber-950">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}
      {loading && <p role="status" className="py-8 text-sm text-gray-600">Loading visual safety…</p>}

      {!loading && data && (
        <>
          <section aria-label="Camera coverage" className="mt-5">
            <h2 className="text-sm font-semibold text-gray-900">Camera coverage</h2>
            {data.coverage.length === 0 ? (
              <p className="mt-2 text-sm text-gray-600">No cameras are set up for this site yet. Add them under Cameras and zones.</p>
            ) : (
              <ul className="mt-2 flex flex-wrap gap-2">
                {data.coverage.map((c) => (
                  <li key={c.camera_id} className={`border px-3 py-2 text-sm ${c.state === 'covered' ? 'border-emerald-300 bg-emerald-50 text-emerald-950' : 'border-amber-400 bg-amber-50 text-amber-950'}`}>
                    <span className="font-medium">{c.name}</span>{': '}
                    {c.state === 'covered' ? 'Covered' : 'Unknown coverage: no recent signal or the view is blocked, so nothing is being checked here'}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.caps.demo_enabled && <DemoPanel onChanged={load} />}

          <div role="tablist" aria-label="Visual safety sections" className="mt-6 flex flex-wrap gap-2 border-b border-gray-200">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                className={`h-10 px-3 text-sm ${tab === t.id ? 'border-b-2 border-gray-900 font-semibold text-gray-900' : 'text-gray-600'}`}>
                {t.label} ({counts(t.statuses)})
              </button>
            ))}
            {[['rules', 'Rules'], ['layout', 'Cameras and zones']].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                className={`h-10 px-3 text-sm ${tab === id ? 'border-b-2 border-gray-900 font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</button>
            ))}
          </div>

          {tab === 'rules' && <RulesEditor rules={data.rules.rules} isDefault={data.rules.is_default} onSaved={load} />}
          {tab === 'layout' && <LayoutEditor layout={data.layout} zoneTypes={data.caps.zone_types} onSaved={load} />}

          {!isConfigTab && (
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
              <ul aria-label="Concerns" className="divide-y divide-gray-200 border border-gray-200 bg-white lg:col-span-2">
                {visible.length === 0 && <li className="p-4 text-sm text-gray-600">Nothing here.</li>}
                {visible.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setSelectedId(c.id)} aria-current={c.id === selectedId ? 'true' : undefined}
                      className={`block w-full p-3 text-left text-sm hover:bg-gray-50 ${c.id === selectedId ? 'bg-gray-50' : ''}`}>
                      <span className="block font-medium text-gray-900">{c.title}</span>
                      <span className="block text-gray-600">{names(c)} · {STATUS_LABELS[c.status] || c.status}{c.staged ? ' · staged' : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="lg:col-span-3">
                {selected
                  ? <ConcernDetail key={`${selected.id}-${selected.status}`} concern={selected} where={names(selected)} onChanged={load} />
                  : <p className="border border-dashed border-gray-300 p-6 text-sm text-gray-600">Select a concern to see what the camera established and what to do next.</p>}
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default VisualSafety;
